import { LightweightElement } from "@/store/lightweight-elements-slice";
import {
  convertToDateString,
  convertToDateTimeString,
} from "@faro-lotv/foundation";
import {
  GENERIC_DATASET_TYPE_HINTS,
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import { isVideoRecordingOrOdometryPath } from "./tree-discriminators";

/**
 * A decision about whether to render an iElement in the 2D tree.
 *
 * Additionally allows to adjust the string and icon used for rendering this element.
 */
export interface ITreeRenderDecision {
  /**
   * Should the element itself be rendered in the tree?
   *
   * This does _not_ influence the rendering of the element's children.
   */
  shouldKeepElement: boolean;

  /**
   * Should the element's children be included in the tree?
   *
   * Whether the children are _rendered_ in the tree is decided
   * again with this function.
   * However, if this is `false`, all children will be discarded.
   */
  shouldKeepChildren: boolean;

  /** A custom display name to use for a node */
  displayName?: string;
}

/**
 * @returns Shows a flat list of all Locations with timestamps as their children.
 * @param iElement IElement to use as base for the filtering
 * @param parentType Parent type of the given IElement
 * @param videoModeCopyTagId id of the tag identifying rooms copied for video mode datasets
 * @param customDate Custom date to use instead of the element's createdAt date
 */
function locationFilter(
  iElement: IElement | LightweightElement,
  parentType?: string,
  videoModeCopyTagId?: GUID,
  customDate?: string,
): ITreeRenderDecision {
  const date = customDate ?? iElement.createdAt;
  if (iElement.type === IElementType.section) {
    if (parentType === IElementType.timeSeries) {
      const videoModeCopy =
        !!videoModeCopyTagId &&
        !!iElement.labelIds?.some((label) => label === videoModeCopyTagId);

      let displayName = convertToDateString(date);

      if (iElement.typeHint === IElementTypeHint.dataSession) {
        displayName += ` - ${iElement.name}`;
      }
      return {
        // Stop recursive rendering of tree at this level
        shouldKeepChildren: false,

        shouldKeepElement: !videoModeCopy,

        displayName,
      };
    }

    return {
      displayName:
        iElement.typeHint &&
        Object.values<string>(GENERIC_DATASET_TYPE_HINTS).includes(
          iElement.typeHint,
        )
          ? `${convertToDateString(date)} - ${iElement.name}`
          : undefined,
      shouldKeepElement: true,
      shouldKeepChildren: true,
    };
  }

  if (
    iElement.type === IElementType.timeSeries &&
    iElement.typeHint === IElementTypeHint.room
  ) {
    return {
      shouldKeepChildren: false,
      shouldKeepElement: false,
    };
  }

  // Parent of all the 360 Images
  if (
    iElement.type === IElementType.group &&
    iElement.typeHint === IElementTypeHint.rooms &&
    iElement.childrenIds?.length
  ) {
    return {
      shouldKeepElement: false,
      shouldKeepChildren: true,
      displayName: "360° Photos",
    };
  }

  // Don't show the TimeSeries if it has no children
  if (
    iElement.type === IElementType.timeSeries &&
    iElement.typeHint === IElementTypeHint.dataSession &&
    iElement.childrenIds?.length
  ) {
    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
      displayName: "4D Sessions",
    };
  }

  // Ignore other elements, but keep their children
  return {
    shouldKeepElement: false,
    shouldKeepChildren: true,
  };
}

/**
 * @returns a list of elements to be shown under video recordings.
 * @param iElement IElement to use as base for the filtering
 */
function videoRecordingsFilter(
  iElement: IElement | LightweightElement,
): ITreeRenderDecision {
  if (
    iElement.type === IElementType.section &&
    iElement.typeHint === IElementTypeHint.odometryPath
  ) {
    // Use the creation date of the ancestor while displaying
    const displayNameWithDate = `${convertToDateTimeString(
      iElement.createdAt,
    )} - ${iElement.name}`;

    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
      displayName: displayNameWithDate,
    };
  } else if (
    iElement.type === IElementType.timeSeries &&
    iElement.typeHint === IElementTypeHint.videoRecordings
  ) {
    // Parent of all video recordings, hence keep the element
    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
      displayName: "Video recordings",
    };
  } else if (
    iElement.type === IElementType.section &&
    iElement.typeHint === IElementTypeHint.videoRecordings
  ) {
    // Use the creation date of the element while displaying
    const displayNameWithDate = `${convertToDateTimeString(
      iElement.createdAt,
    )} - ${iElement.name}`;

    return {
      shouldKeepElement: true,
      shouldKeepChildren: false,
      displayName: displayNameWithDate,
    };
  }
  // Ignore other elements, but keep their children
  return {
    shouldKeepElement: false,
    shouldKeepChildren: true,
  };
}

/**
 * @returns Shows tree of sheets at the top level and the locations being their children
 *    Also, display the timeseries elements of the locations
 * @param iElement IElement to use as base for the filtering
 * @param parentType Parent type of the given iElement
 * @param videoModeCopyTagId ID of label tagging video mode copies
 * @param showTimePoints Show location time points
 * @param customDate Custom date to use instead of the element's createdAt date
 */
export function sheetsFilter(
  iElement: IElement | LightweightElement,
  parentType?: string,
  videoModeCopyTagId?: GUID,
  customDate?: string,
): ITreeRenderDecision {
  if (iElement.type === IElementType.section) {
    const { typeHint } = iElement;

    if (typeHint === IElementTypeHint.area) {
      // Keep the parent of the floor plan pdfs folder
      return {
        shouldKeepElement: true,
        shouldKeepChildren: true,
      };
    }

    if (
      // do not display CAD's in Capture tree
      typeHint === IElementTypeHint.bimModel
    ) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }

    if (typeHint === IElementTypeHint.cluster) {
      // Do not show the clusters in the project tree
      return {
        shouldKeepElement: false,
        shouldKeepChildren: true,
      };
    }

    if (
      typeHint === IElementTypeHint.focusScan ||
      typeHint === IElementTypeHint.orbisScan ||
      typeHint === IElementTypeHint.elsScan ||
      typeHint === IElementTypeHint.focusPanoCalibrationScan ||
      typeHint === IElementTypeHint.focusPanoScan ||
      typeHint === IElementTypeHint.photogrammetryScan ||
      typeHint === IElementTypeHint.roviScan
    ) {
      // Do not show the children of the scan sections in the project tree
      return {
        shouldKeepElement: true,
        shouldKeepChildren: false,
      };
    }

    // Do not show registration scans in the project tree
    if (typeHint === IElementTypeHint.registrationScan) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }

    // Do not show the Section - Layer in the project tree
    // It is added in the iElement tree structure to carry the multiple formats of floorplans generated by customer
    if (typeHint === IElementTypeHint.layer) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }
  }

  if (iElement.type === IElementType.cluster) {
    if (iElement.typeHint === IElementTypeHint.rootCluster) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: true,
      };
    }

    // For the SiteScape capture clusters, we want to show the cluster in the tree but not its children as scans
    if (iElement.typeHint === IElementTypeHint.siteScapeCapture) {
      return {
        shouldKeepElement: true,
        shouldKeepChildren: false,
      };
    }

    // Keep the clusters
    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
    };
  }

  if (isVideoRecordingOrOdometryPath(iElement.typeHint)) {
    return videoRecordingsFilter(iElement);
  }

  return locationFilter(iElement, parentType, videoModeCopyTagId, customDate);
}
